import "./ServiceOrderDetailPage.css";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

type ServiceOrderStatus =
  | "received"
  | "inspection"
  | "diagnosing"
  | "awaiting_approval"
  | "approved"
  | "in_progress"
  | "testing"
  | "ready"
  | "delivered"
  | "cancelled";

type OrderData = {
  id: string;
  organization_id: string;
  customer_id: string;
  asset_id: string | null;
  order_number: string;
  status: ServiceOrderStatus;
  priority: string;
  customer_report: string | null;
  customer_requested_service: string | null;
  initial_condition: string | null;
  accessories_received: string | null;
  intake_notes: string | null;
  diagnosis: string | null;
  diagnosis_notes: string | null;
  estimated_total: number | string;
  approved_total: number | string;
  final_total: number | string;
  estimated_delivery_at: string | null;
  received_at: string;
  diagnosed_at: string | null;
  ready_at: string | null;
  delivered_at: string | null;
  warranty_days: number;
  warranty_until: string | null;
  warranty_notes: string | null;
};

type CustomerData = {
  id: string;
  name: string;
  document_type: string | null;
  document_number: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
};

type AssetData = {
  id: string;
  asset_type: string;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  identifier: string | null;
};

type LaborRow = {
  id: string;
  description: string;
  quantity: number | string;
  unit_price: number | string;
  discount_amount: number | string;
  total: number | string;
  notes: string | null;
  created_at: string;
};

type PartRow = {
  id: string;
  description: string;
  quantity: number | string;
  unit_cost: number | string;
  unit_price: number | string;
  total: number | string;
  part_status: string;
  notes: string | null;
  created_at: string;
};

type HistoryRow = {
  id: string;
  previous_status: ServiceOrderStatus | null;
  new_status: ServiceOrderStatus;
  note: string | null;
  changed_by_email: string | null;
  created_at: string;
};

type EvidenceRow = {
  id: string;
  evidence_stage: string;
  media_type: "image" | "video" | "document";
  storage_bucket: string;
  storage_path: string;
  original_filename: string | null;
  mime_type: string | null;
  file_size: number | string | null;
  description: string | null;
  visible_to_customer: boolean;
  is_initial_evidence: boolean;
  captured_at: string;
};

type DeliveryData = {
  id: string;
  delivered_to: string | null;
  recipient_document: string | null;
  final_condition: string | null;
  delivery_notes: string | null;
  customer_conformity: boolean | null;
  delivered_at: string;
};

type ApprovalData = {
  id: string;
  version: number;
  status: "pending" | "approved" | "rejected" | "expired" | "cancelled";
  title: string;
  description: string | null;
  previous_total: number | string;
  additional_amount: number | string;
  proposed_total: number | string;
  approval_token: string;
  requested_at: string;
  expires_at: string | null;
  decided_at: string | null;
  customer_comment: string | null;
};

type TechnicianData = {
  id: string;
  user_id: string;
  email: string | null;
  is_primary: boolean;
  assigned_at: string;
};

type AvailableTechnicianData = {
  user_id: string;
  email: string;
  role: string;
};

type WarehouseRow = {
  id: string;
  name: string;
};

type LiveTotals = {
  labor: number | string;
  parts: number | string;
  total: number | string;
};

type ServiceOrderDetail = {
  order: OrderData;
  customer: CustomerData | null;
  asset: AssetData | null;
  labor: LaborRow[];
  parts: PartRow[];
  history: HistoryRow[];
  evidence: EvidenceRow[];
  delivery: DeliveryData | null;
  approval: ApprovalData | null;
  technicians: TechnicianData[];
  available_technicians: AvailableTechnicianData[];
  available_assets: AssetData[];
  warehouses: WarehouseRow[];
  live_totals: LiveTotals;
};

type LaborForm = {
  description: string;
  quantity: string;
  unitPrice: string;
  discountAmount: string;
  notes: string;
};

type PartForm = {
  description: string;
  quantity: string;
  unitCost: string;
  unitPrice: string;
  notes: string;
};

const EMPTY_LABOR: LaborForm = {
  description: "",
  quantity: "1",
  unitPrice: "0",
  discountAmount: "0",
  notes: "",
};

const EMPTY_PART: PartForm = {
  description: "",
  quantity: "1",
  unitCost: "0",
  unitPrice: "0",
  notes: "",
};

const STATUS_LABELS: Record<ServiceOrderStatus, string> = {
  received: "Recibida",
  inspection: "Inspección",
  diagnosing: "Diagnóstico",
  awaiting_approval: "Esperando aprobación",
  approved: "Aprobada",
  in_progress: "En servicio",
  testing: "Pruebas",
  ready: "Terminada",
  delivered: "Entregada",
  cancelled: "Cancelada",
};

function statusActions(status: ServiceOrderStatus) {
  switch (status) {
    case "received":
      return [
        ["inspection", "Iniciar inspección"],
        ["diagnosing", "Diagnosticar"],
        ["cancelled", "Cancelar"],
      ] as const;
    case "inspection":
      return [
        ["diagnosing", "Diagnosticar"],
        ["cancelled", "Cancelar"],
      ] as const;
    case "diagnosing":
      return [
        ["awaiting_approval", "Solicitar aprobación"],
        ["cancelled", "Cancelar"],
      ] as const;
    case "awaiting_approval":
      return [
        ["approved", "Aprobar servicio"],
        ["cancelled", "Cancelar"],
      ] as const;
    case "approved":
      return [
        ["in_progress", "Iniciar servicio"],
        ["cancelled", "Cancelar"],
      ] as const;
    case "in_progress":
      return [
        ["testing", "Pasar a pruebas"],
        ["ready", "Marcar terminada"],
        ["cancelled", "Cancelar"],
      ] as const;
    case "testing":
      return [
        ["in_progress", "Volver a servicio"],
        ["ready", "Marcar terminada"],
        ["cancelled", "Cancelar"],
      ] as const;
    case "ready":
      return [] as const;
    default:
      return [] as const;
  }
}

function money(value: number | string) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(Number(value || 0));
}

function datetime(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function fileSize(value: number | string | null) {
  const bytes = Number(value ?? 0);
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function ServiceOrderDetailPage() {
  const { orderId = "" } = useParams();
  const { selectedOrganization } = useOrganization();

  const [detail, setDetail] = useState<ServiceOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [trackingLink, setTrackingLink] = useState("");
  const [trackingCopied, setTrackingCopied] = useState(false);

  const [assetId, setAssetId] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [diagnosisNotes, setDiagnosisNotes] = useState("");
  const [estimatedDelivery, setEstimatedDelivery] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [intakeCustomerReport, setIntakeCustomerReport] = useState("");
  const [intakeRequestedService, setIntakeRequestedService] = useState("");
  const [intakeInitialCondition, setIntakeInitialCondition] = useState("");
  const [intakeAccessories, setIntakeAccessories] = useState("");
  const [intakeNotes, setIntakeNotes] = useState("");

  const [laborForm, setLaborForm] = useState<LaborForm>(EMPTY_LABOR);
  const [partForm, setPartForm] = useState<PartForm>(EMPTY_PART);
  const [warehouseId, setWarehouseId] = useState("");

  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [evidenceStage, setEvidenceStage] = useState("diagnosis");
  const [evidenceDescription, setEvidenceDescription] = useState("");
  const [evidenceVisible, setEvidenceVisible] = useState(false);
  const [evidenceInitial, setEvidenceInitial] = useState(false);
  const [evidenceLinks, setEvidenceLinks] = useState<Record<string, string>>({});

  const [deliveredTo, setDeliveredTo] = useState("");
  const [recipientDocument, setRecipientDocument] = useState("");
  const [finalCondition, setFinalCondition] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [customerConformity, setCustomerConformity] = useState(true);
  const [warrantyDays, setWarrantyDays] = useState("0");
  const [warrantyNotes, setWarrantyNotes] = useState("");

  const [approvalTitle, setApprovalTitle] = useState("");
  const [approvalDescription, setApprovalDescription] = useState("");
  const [approvalAmount, setApprovalAmount] = useState("0");
  const [approvalDays, setApprovalDays] = useState("7");
  const [approvalCopied, setApprovalCopied] = useState(false);

  const [technicianId, setTechnicianId] = useState("");
  const [technicianPrimary, setTechnicianPrimary] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!orderId) {
      setLoadError("Orden inválida.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    const { data, error } = await supabase.rpc("get_service_order_detail", {
      p_service_order_id: orderId,
    });

    if (error) {
      setDetail(null);
      setLoadError(error.message);
      setLoading(false);
      return;
    }

    const next = data as ServiceOrderDetail;

    if (
      selectedOrganization &&
      next.order.organization_id !== selectedOrganization.organizationId
    ) {
      setDetail(null);
      setLoadError("La orden no pertenece a la empresa seleccionada.");
      setLoading(false);
      return;
    }

    setDetail(next);
    setAssetId(next.order.asset_id ?? "");
    setDiagnosis(next.order.diagnosis ?? "");
    setDiagnosisNotes(next.order.diagnosis_notes ?? "");
    setIntakeCustomerReport(next.order.customer_report ?? "");
    setIntakeRequestedService(next.order.customer_requested_service ?? "");
    setIntakeInitialCondition(next.order.initial_condition ?? "");
    setIntakeAccessories(next.order.accessories_received ?? "");
    setIntakeNotes(next.order.intake_notes ?? "");
    setEstimatedDelivery(
      next.order.estimated_delivery_at
        ? new Date(next.order.estimated_delivery_at).toISOString().slice(0, 16)
        : "",
    );

    const links = await Promise.all(
      next.evidence.map(async (evidence) => {
        const { data: signed } = await supabase.storage
          .from(evidence.storage_bucket)
          .createSignedUrl(evidence.storage_path, 3600);

        return [evidence.id, signed?.signedUrl ?? ""] as const;
      }),
    );

    setEvidenceLinks(
      Object.fromEntries(links.filter(([, url]) => Boolean(url))),
    );
    setLoading(false);
  }, [orderId, selectedOrganization]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const isClosed =
    detail?.order.status === "delivered" || detail?.order.status === "cancelled";

  const canManageTechnicians = Boolean(
    selectedOrganization &&
      ["owner", "admin", "manager", "platform_admin"].includes(
        selectedOrganization.role,
      ),
  );

  const proposedParts = useMemo(
    () =>
      detail?.parts.filter(
        (part) => !["used", "rejected", "returned"].includes(part.part_status),
      ).length ?? 0,
    [detail],
  );

  async function runAction(
    key: string,
    action: () => Promise<{ error: { message: string } | null }>,
    success: string,
  ) {
    setBusy(key);
    setActionError(null);
    setActionSuccess(null);

    const result = await action();
    setBusy(null);

    if (result.error) {
      setActionError(result.error.message);
      return false;
    }

    setActionSuccess(success);
    await loadDetail();
    return true;
  }

  async function createTrackingLink() {
    setBusy("tracking");
    setActionError(null);
    setActionSuccess(null);

    const { data, error } = await supabase.rpc("create_service_tracking_link", {
      p_service_order_id: orderId,
    });

    setBusy(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    const url = `${window.location.origin}/seguimiento/${data}`;
    setTrackingLink(url);

    try {
      await navigator.clipboard.writeText(url);
      setTrackingCopied(true);
      window.setTimeout(() => setTrackingCopied(false), 2500);
      setActionSuccess("Enlace de seguimiento copiado.");
    } catch {
      setActionSuccess("Enlace de seguimiento generado.");
    }
  }

  async function saveAsset() {
    await runAction(
      "asset",
      async () => {
        const { error } = await supabase.rpc("set_service_order_asset", {
          p_service_order_id: orderId,
          p_asset_id: assetId || null,
        });
        return { error };
      },
      "Activo asociado actualizado.",
    );
  }

  async function saveIntake(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    await runAction(
      "intake",
      async () => {
        const { error } = await supabase.rpc("update_service_order_intake", {
          p_service_order_id: orderId,
          p_customer_report: intakeCustomerReport.trim() || null,
          p_customer_requested_service: intakeRequestedService.trim() || null,
          p_initial_condition: intakeInitialCondition.trim() || null,
          p_accessories_received: intakeAccessories.trim() || null,
          p_intake_notes: intakeNotes.trim() || null,
        });
        return { error };
      },
      "Datos de ingreso actualizados.",
    );
  }

  async function saveDiagnosis(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!diagnosis.trim()) {
      setActionError("Ingresa el diagnóstico.");
      return;
    }

    await runAction(
      "diagnosis",
      async () => {
        const { error } = await supabase.rpc("update_service_order_diagnosis", {
          p_service_order_id: orderId,
          p_diagnosis: diagnosis,
          p_diagnosis_notes: diagnosisNotes.trim() || null,
          p_estimated_delivery_at: estimatedDelivery
            ? new Date(estimatedDelivery).toISOString()
            : null,
        });
        return { error };
      },
      "Diagnóstico actualizado.",
    );
  }

  async function addLabor(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const quantity = Number(laborForm.quantity);
    const unitPrice = Number(laborForm.unitPrice);
    const discount = Number(laborForm.discountAmount);

    if (
      !laborForm.description.trim() ||
      !Number.isFinite(quantity) ||
      quantity <= 0 ||
      !Number.isFinite(unitPrice) ||
      unitPrice < 0 ||
      !Number.isFinite(discount) ||
      discount < 0
    ) {
      setActionError("Revisa los datos de mano de obra.");
      return;
    }

    const ok = await runAction(
      "labor",
      async () => {
        const { error } = await supabase.rpc("add_service_order_labor", {
          p_service_order_id: orderId,
          p_description: laborForm.description,
          p_quantity: quantity,
          p_unit_price: unitPrice,
          p_discount_amount: discount,
          p_catalog_item_id: null,
          p_notes: laborForm.notes.trim() || null,
        });
        return { error };
      },
      "Mano de obra agregada.",
    );

    if (ok) setLaborForm(EMPTY_LABOR);
  }

  async function addPart(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const quantity = Number(partForm.quantity);
    const unitCost = Number(partForm.unitCost);
    const unitPrice = Number(partForm.unitPrice);

    if (
      !partForm.description.trim() ||
      !Number.isFinite(quantity) ||
      quantity <= 0 ||
      !Number.isFinite(unitCost) ||
      unitCost < 0 ||
      !Number.isFinite(unitPrice) ||
      unitPrice < 0
    ) {
      setActionError("Revisa los datos del repuesto.");
      return;
    }

    const ok = await runAction(
      "part",
      async () => {
        const { error } = await supabase.rpc("add_service_order_part", {
          p_service_order_id: orderId,
          p_description: partForm.description,
          p_quantity: quantity,
          p_unit_cost: unitCost,
          p_unit_price: unitPrice,
          p_catalog_item_id: null,
          p_notes: partForm.notes.trim() || null,
        });
        return { error };
      },
      "Repuesto agregado.",
    );

    if (ok) setPartForm(EMPTY_PART);
  }

  async function consumeParts() {
    if (!warehouseId) {
      setActionError("Selecciona un almacén.");
      return;
    }

    await runAction(
      "consume",
      async () => {
        const { error } = await supabase.rpc("consume_service_order_parts", {
          p_service_order_id: orderId,
          p_warehouse_id: warehouseId,
        });
        return { error };
      },
      "Consumo de repuestos procesado.",
    );
  }

  async function transitionStatus(nextStatus: ServiceOrderStatus) {
    await runAction(
      `status:${nextStatus}`,
      async () => {
        const { error } = await supabase.rpc("transition_service_order_status", {
          p_service_order_id: orderId,
          p_new_status: nextStatus,
          p_note: statusNote.trim() || null,
        });
        return { error };
      },
      `Estado actualizado a ${STATUS_LABELS[nextStatus]}.`,
    );

    setStatusNote("");
  }

  async function assignTechnician() {
    if (!technicianId) {
      setActionError("Selecciona un técnico.");
      return;
    }

    const ok = await runAction(
      "technician",
      async () => {
        const { error } = await supabase.rpc("assign_service_order_technician", {
          p_service_order_id: orderId,
          p_user_id: technicianId,
          p_is_primary: technicianPrimary,
        });
        return { error };
      },
      "Técnico asignado correctamente.",
    );

    if (ok) {
      setTechnicianId("");
      setTechnicianPrimary(false);
    }
  }

  async function removeTechnician(userId: string) {
    await runAction(
      `technician-remove:${userId}`,
      async () => {
        const { error } = await supabase.rpc("remove_service_order_technician", {
          p_service_order_id: orderId,
          p_user_id: userId,
        });
        return { error };
      },
      "Técnico retirado de la orden.",
    );
  }

  async function createApproval(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!approvalTitle.trim()) {
      setActionError("Ingresa un título para la aprobación.");
      return;
    }

    const amount = Number(approvalAmount);
    const days = Number(approvalDays);

    if (!Number.isFinite(amount) || amount < 0) {
      setActionError("El monto adicional no es válido.");
      return;
    }

    if (!Number.isInteger(days) || days < 1 || days > 90) {
      setActionError("La vigencia debe estar entre 1 y 90 días.");
      return;
    }

    const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000);

    const ok = await runAction(
      "approval",
      async () => {
        const { error } = await supabase.rpc(
          "create_service_approval_request",
          {
            p_service_order_id: orderId,
            p_title: approvalTitle,
            p_description: approvalDescription.trim() || null,
            p_additional_amount: amount,
            p_expires_at: expiresAt.toISOString(),
          },
        );
        return { error };
      },
      "Solicitud de aprobación creada.",
    );

    if (ok) {
      setApprovalTitle("");
      setApprovalDescription("");
      setApprovalAmount("0");
      setApprovalDays("7");
      setApprovalCopied(false);
    }
  }

  async function copyApprovalLink() {
    if (!detail?.approval?.approval_token) return;

    const url =
      window.location.origin +
      "/service-approval?token=" +
      encodeURIComponent(detail.approval.approval_token);

    try {
      await navigator.clipboard.writeText(url);
      setApprovalCopied(true);
      window.setTimeout(() => setApprovalCopied(false), 2500);
    } catch {
      setActionError("No se pudo copiar el enlace.");
    }
  }

  async function recordDelivery(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!deliveredTo.trim()) {
      setActionError("Ingresa quién recibe el activo.");
      return;
    }

    const days = Number(warrantyDays);

    if (!Number.isInteger(days) || days < 0 || days > 3650) {
      setActionError("Los días de garantía deben estar entre 0 y 3650.");
      return;
    }

    const ok = await runAction(
      "delivery",
      async () => {
        const { error } = await supabase.rpc("record_service_delivery", {
          p_service_order_id: orderId,
          p_delivered_to: deliveredTo,
          p_recipient_document: recipientDocument.trim() || null,
          p_final_condition: finalCondition.trim() || null,
          p_delivery_notes: deliveryNotes.trim() || null,
          p_customer_conformity: customerConformity,
          p_warranty_days: days,
          p_warranty_notes: warrantyNotes.trim() || null,
        });
        return { error };
      },
      "Entrega registrada correctamente.",
    );

    if (ok) {
      setDeliveredTo("");
      setRecipientDocument("");
      setFinalCondition("");
      setDeliveryNotes("");
      setCustomerConformity(true);
      setWarrantyDays("0");
      setWarrantyNotes("");
    }
  }

  async function uploadEvidence(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!evidenceFile) {
      setActionError("Selecciona un archivo.");
      return;
    }

    if (evidenceFile.size > 50 * 1024 * 1024) {
      setActionError("El archivo supera el límite de 50 MB.");
      return;
    }

    const form = new FormData();
    form.append("file", evidenceFile);
    form.append("service_order_id", orderId);
    form.append("evidence_stage", evidenceStage);
    form.append("description", evidenceDescription);
    form.append("visible_to_customer", String(evidenceVisible));
    form.append("is_initial_evidence", String(evidenceInitial));

    setBusy("evidence");
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.functions.invoke("service-evidence-upload", {
      body: form,
    });

    setBusy(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    setEvidenceFile(null);
    setEvidenceDescription("");
    setEvidenceVisible(false);
    setEvidenceInitial(false);
    setActionSuccess("Evidencia subida correctamente.");
    await loadDetail();
  }

  if (loading) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Cargando orden…</strong>
          <p>Preparando el detalle operativo.</p>
        </section>
      </div>
    );
  }

  if (loadError || !detail) {
    return (
      <div className="page">
        <Link className="compact-button detail-back-link" to="/taller">
          ← Volver a Taller
        </Link>
        <section className="empty-state empty-state-error">
          <strong>No se pudo abrir la orden.</strong>
          <p>{loadError ?? "Orden no disponible."}</p>
        </section>
      </div>
    );
  }

  const { order, customer, asset, labor, parts, history, evidence } = detail;

  return (
    <div className="page">
      <div className="detail-topline">
        <Link className="compact-button detail-back-link" to="/taller">
          ← Volver a Taller
        </Link>
        <div className="detail-topline-actions">
          <button
            className="compact-button"
            type="button"
            disabled={busy === "tracking"}
            onClick={() => void createTrackingLink()}
          >
            {busy === "tracking"
              ? "Generando…"
              : trackingCopied
                ? "Enlace copiado"
                : "Compartir seguimiento"}
          </button>
          <span className={`status-badge service-status-${order.status}`}>
            {STATUS_LABELS[order.status]}
          </span>
        </div>
      </div>

      {trackingLink ? (
        <div className="tracking-link-box">
          <strong>Enlace del cliente</strong>
          <span>{trackingLink}</span>
        </div>
      ) : null}

      <header className="page-header detail-header">
        <div>
          <span className="eyebrow">Orden de servicio</span>
          <h1>{order.order_number}</h1>
          <p>{customer?.name ?? "Cliente sin información"}</p>
        </div>
        <div className="detail-total">
          <span>Total operativo</span>
          <strong>{money(detail.live_totals.total)}</strong>
        </div>
      </header>

      {actionError ? (
        <div className="inline-alert" role="alert">
          {actionError}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="form-success detail-message">{actionSuccess}</div>
      ) : null}

      <section className="detail-metric-grid">
        <div className="metric-card">
          <span>Mano de obra</span>
          <strong>{money(detail.live_totals.labor)}</strong>
        </div>
        <div className="metric-card">
          <span>Repuestos</span>
          <strong>{money(detail.live_totals.parts)}</strong>
        </div>
        <div className="metric-card">
          <span>Aprobado</span>
          <strong>{money(order.approved_total)}</strong>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Recepción</span>
            <h2>Ficha completa de ingreso</h2>
          </div>
        </div>

        <form className="service-intake-form" onSubmit={saveIntake}>
          <label>
            Motivo / reporte del cliente
            <textarea
              value={intakeCustomerReport}
              disabled={Boolean(isClosed)}
              onChange={(event) => setIntakeCustomerReport(event.target.value)}
            />
          </label>
          <label>
            Servicio solicitado
            <textarea
              value={intakeRequestedService}
              disabled={Boolean(isClosed)}
              onChange={(event) => setIntakeRequestedService(event.target.value)}
            />
          </label>
          <label>
            Condición inicial
            <textarea
              value={intakeInitialCondition}
              disabled={Boolean(isClosed)}
              onChange={(event) => setIntakeInitialCondition(event.target.value)}
            />
          </label>
          <label>
            Accesorios recibidos
            <textarea
              value={intakeAccessories}
              disabled={Boolean(isClosed)}
              onChange={(event) => setIntakeAccessories(event.target.value)}
            />
          </label>
          <label className="detail-span-full">
            Observaciones de ingreso
            <textarea
              value={intakeNotes}
              disabled={Boolean(isClosed)}
              onChange={(event) => setIntakeNotes(event.target.value)}
            />
          </label>
          <div className="form-actions detail-span-full">
            <button
              className="primary-button"
              type="submit"
              disabled={Boolean(isClosed) || busy === "intake"}
            >
              {busy === "intake" ? "Guardando…" : "Guardar ingreso"}
            </button>
          </div>
        </form>
      </section>

      <section className="detail-grid">
        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Cliente y recepción</span>
              <h2>Datos de ingreso</h2>
            </div>
          </div>

          <dl className="detail-definition-grid">
            <div>
              <dt>Cliente</dt>
              <dd>
                {customer ? (
                  <Link className="record-inline-link" to={`/clientes/${customer.id}`}>
                    {customer.name}
                  </Link>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <div>
              <dt>Documento</dt>
              <dd>
                {customer?.document_type && customer.document_number
                  ? `${customer.document_type} ${customer.document_number}`
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Contacto</dt>
              <dd>{customer?.whatsapp ?? customer?.phone ?? customer?.email ?? "—"}</dd>
            </div>
            <div>
              <dt>Ingreso</dt>
              <dd>{datetime(order.received_at)}</dd>
            </div>
            <div className="detail-span-full">
              <dt>Reporte del cliente</dt>
              <dd>{order.customer_report ?? "—"}</dd>
            </div>
            <div className="detail-span-full">
              <dt>Servicio solicitado</dt>
              <dd>{order.customer_requested_service ?? "—"}</dd>
            </div>
          </dl>
        </article>

        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Activo</span>
              <h2>Bien asociado</h2>
            </div>
          </div>

          <div className="detail-control-stack">
            <select
              value={assetId}
              disabled={Boolean(isClosed)}
              onChange={(event) => setAssetId(event.target.value)}
            >
              <option value="">Sin activo asociado</option>
              {detail.available_assets.map((item) => (
                <option key={item.id} value={item.id}>
                  {[item.asset_type, item.brand, item.model, item.identifier]
                    .filter(Boolean)
                    .join(" · ")}
                </option>
              ))}
            </select>
            <button
              className="compact-button"
              type="button"
              disabled={busy === "asset" || Boolean(isClosed)}
              onClick={() => void saveAsset()}
            >
              {busy === "asset" ? "Guardando…" : "Guardar activo"}
            </button>
          </div>

          {asset ? (
            <dl className="detail-definition-grid detail-asset-summary">
              <div>
                <dt>Tipo</dt>
                <dd>{asset.asset_type}</dd>
              </div>
              <div>
                <dt>Marca / modelo</dt>
                <dd>{[asset.brand, asset.model].filter(Boolean).join(" ") || "—"}</dd>
              </div>
              <div>
                <dt>Serie</dt>
                <dd>{asset.serial_number ?? "—"}</dd>
              </div>
              <div>
                <dt>Identificador</dt>
                <dd>{asset.identifier ?? "—"}</dd>
              </div>
            </dl>
          ) : null}
        </article>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Equipo técnico</span>
            <h2>Responsables de la orden</h2>
          </div>
        </div>

        {canManageTechnicians && !isClosed ? (
          <div className="technician-assign-row">
            <select
              value={technicianId}
              onChange={(event) => setTechnicianId(event.target.value)}
            >
              <option value="">Selecciona un técnico</option>
              {detail.available_technicians
                .filter(
                  (candidate) =>
                    !detail.technicians.some(
                      (assigned) => assigned.user_id === candidate.user_id,
                    ),
                )
                .map((candidate) => (
                  <option key={candidate.user_id} value={candidate.user_id}>
                    {candidate.email} · {candidate.role}
                  </option>
                ))}
            </select>

            <label className="detail-checkbox technician-primary-check">
              <input
                type="checkbox"
                checked={technicianPrimary}
                onChange={(event) => setTechnicianPrimary(event.target.checked)}
              />
              Principal
            </label>

            <button
              className="compact-button"
              type="button"
              disabled={busy === "technician"}
              onClick={() => void assignTechnician()}
            >
              {busy === "technician" ? "Asignando…" : "Asignar"}
            </button>
          </div>
        ) : null}

        <div className="technician-list">
          {detail.technicians.length === 0 ? (
            <p className="settings-empty">No hay técnicos asignados.</p>
          ) : (
            detail.technicians.map((technician) => (
              <div className="technician-row" key={technician.id}>
                <div>
                  <strong>{technician.email ?? technician.user_id}</strong>
                  <span>
                    {technician.is_primary ? "Técnico principal" : "Técnico asignado"} ·{" "}
                    {datetime(technician.assigned_at)}
                  </span>
                </div>

                <div className="technician-row-actions">
                  {technician.is_primary ? (
                    <span className="status-badge status-badge-approved">
                      Principal
                    </span>
                  ) : null}

                  {canManageTechnicians && !isClosed ? (
                    <button
                      className="compact-button compact-button-danger"
                      type="button"
                      disabled={busy === `technician-remove:${technician.user_id}`}
                      onClick={() => void removeTechnician(technician.user_id)}
                    >
                      {busy === `technician-remove:${technician.user_id}`
                        ? "Retirando…"
                        : "Retirar"}
                    </button>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Estado</span>
            <h2>Avance de la orden</h2>
          </div>
        </div>

        <div className="status-note-row">
          <input
            placeholder="Nota opcional para el cambio de estado"
            value={statusNote}
            onChange={(event) => setStatusNote(event.target.value)}
          />
          <div className="quotation-actions detail-status-actions">
            {statusActions(order.status).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={
                  value === "cancelled"
                    ? "compact-button compact-button-danger"
                    : "compact-button"
                }
                disabled={busy === `status:${value}`}
                onClick={() => void transitionStatus(value)}
              >
                {busy === `status:${value}` ? "Actualizando…" : label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Diagnóstico</span>
            <h2>Evaluación técnica</h2>
          </div>
        </div>

        <form className="detail-diagnosis-form" onSubmit={saveDiagnosis}>
          <label className="detail-span-full">
            Diagnóstico
            <textarea
              required
              rows={3}
              disabled={Boolean(isClosed)}
              value={diagnosis}
              onChange={(event) => setDiagnosis(event.target.value)}
            />
          </label>
          <label className="detail-span-full">
            Notas técnicas
            <textarea
              rows={3}
              disabled={Boolean(isClosed)}
              value={diagnosisNotes}
              onChange={(event) => setDiagnosisNotes(event.target.value)}
            />
          </label>
          <label>
            Entrega estimada
            <input
              type="datetime-local"
              disabled={Boolean(isClosed)}
              value={estimatedDelivery}
              onChange={(event) => setEstimatedDelivery(event.target.value)}
            />
          </label>
          <div className="form-actions">
            <button
              className="primary-button"
              type="submit"
              disabled={busy === "diagnosis" || Boolean(isClosed)}
            >
              {busy === "diagnosis" ? "Guardando…" : "Guardar diagnóstico"}
            </button>
          </div>
        </form>
      </section>

      <section className="panel-card approval-internal-panel">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Aprobación del cliente</span>
            <h2>Trabajos adicionales</h2>
          </div>
          {detail.approval ? (
            <span className={`approval-internal-status approval-internal-${detail.approval.status}`}>
              {detail.approval.status}
            </span>
          ) : null}
        </div>

        {detail.approval ? (
          <div className="approval-internal-summary">
            <div>
              <strong>{detail.approval.title}</strong>
              <span>
                Versión {detail.approval.version} · solicitado {datetime(detail.approval.requested_at)}
              </span>
            </div>
            <div className="approval-internal-money">
              <span>Anterior {money(detail.approval.previous_total)}</span>
              <span>Adicional {money(detail.approval.additional_amount)}</span>
              <strong>Propuesto {money(detail.approval.proposed_total)}</strong>
            </div>
            {detail.approval.description ? (
              <p>{detail.approval.description}</p>
            ) : null}
            {detail.approval.customer_comment ? (
              <p>
                <strong>Comentario del cliente:</strong>{" "}
                {detail.approval.customer_comment}
              </p>
            ) : null}
          </div>
        ) : null}

        {detail.approval?.status === "pending" ? (
          <div className="approval-link-row">
            <input
              readOnly
              value={
                window.location.origin +
                "/service-approval?token=" +
                encodeURIComponent(detail.approval.approval_token)
              }
            />
            <button
              className="compact-button"
              type="button"
              onClick={() => void copyApprovalLink()}
            >
              {approvalCopied ? "Copiado" : "Copiar enlace"}
            </button>
          </div>
        ) : null}

        {!isClosed &&
        detail.approval?.status !== "pending" &&
        ["diagnosing", "approved", "in_progress", "testing"].includes(order.status) ? (
          <form className="approval-internal-form" onSubmit={createApproval}>
            <label className="detail-span-2">
              Título
              <input
                required
                placeholder="Ej. Cambio de componente adicional"
                value={approvalTitle}
                onChange={(event) => setApprovalTitle(event.target.value)}
              />
            </label>

            <label className="detail-span-2">
              Descripción
              <textarea
                rows={3}
                value={approvalDescription}
                onChange={(event) => setApprovalDescription(event.target.value)}
              />
            </label>

            <label>
              Monto adicional
              <input
                type="number"
                min="0"
                step="0.01"
                value={approvalAmount}
                onChange={(event) => setApprovalAmount(event.target.value)}
              />
            </label>

            <label>
              Vigencia en días
              <input
                type="number"
                min="1"
                max="90"
                value={approvalDays}
                onChange={(event) => setApprovalDays(event.target.value)}
              />
            </label>

            <div className="form-actions detail-span-full">
              <button
                className="primary-button"
                type="submit"
                disabled={busy === "approval"}
              >
                {busy === "approval" ? "Creando…" : "Crear solicitud"}
              </button>
            </div>
          </form>
        ) : null}

        {!detail.approval &&
        order.status === "awaiting_approval" ? (
          <div className="inline-alert">
            La orden está esperando aprobación, pero no existe una solicitud activa.
          </div>
        ) : null}
      </section>

      <section className="detail-grid">
        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Mano de obra</span>
              <h2>Servicios realizados</h2>
            </div>
          </div>

          {!isClosed ? (
            <form className="detail-line-form" onSubmit={addLabor}>
              <input
                required
                placeholder="Descripción"
                value={laborForm.description}
                onChange={(event) =>
                  setLaborForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
              <input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="Cantidad"
                value={laborForm.quantity}
                onChange={(event) =>
                  setLaborForm((current) => ({
                    ...current,
                    quantity: event.target.value,
                  }))
                }
              />
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Precio"
                value={laborForm.unitPrice}
                onChange={(event) =>
                  setLaborForm((current) => ({
                    ...current,
                    unitPrice: event.target.value,
                  }))
                }
              />
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Descuento"
                value={laborForm.discountAmount}
                onChange={(event) =>
                  setLaborForm((current) => ({
                    ...current,
                    discountAmount: event.target.value,
                  }))
                }
              />
              <button className="compact-button" type="submit" disabled={busy === "labor"}>
                {busy === "labor" ? "Agregando…" : "Agregar"}
              </button>
            </form>
          ) : null}

          <div className="detail-list">
            {labor.length === 0 ? (
              <p className="settings-empty">Sin mano de obra registrada.</p>
            ) : (
              labor.map((row) => (
                <div className="detail-list-row" key={row.id}>
                  <div>
                    <strong>{row.description}</strong>
                    <span>
                      {Number(row.quantity)} × {money(row.unit_price)}
                      {Number(row.discount_amount) > 0
                        ? ` · Desc. ${money(row.discount_amount)}`
                        : ""}
                    </span>
                  </div>
                  <strong>{money(row.total)}</strong>
                </div>
              ))
            )}
          </div>
        </article>

        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Repuestos</span>
              <h2>Materiales y piezas</h2>
            </div>
          </div>

          {!isClosed ? (
            <form className="detail-line-form" onSubmit={addPart}>
              <input
                required
                placeholder="Descripción"
                value={partForm.description}
                onChange={(event) =>
                  setPartForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
              <input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="Cantidad"
                value={partForm.quantity}
                onChange={(event) =>
                  setPartForm((current) => ({
                    ...current,
                    quantity: event.target.value,
                  }))
                }
              />
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Costo"
                value={partForm.unitCost}
                onChange={(event) =>
                  setPartForm((current) => ({
                    ...current,
                    unitCost: event.target.value,
                  }))
                }
              />
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Precio"
                value={partForm.unitPrice}
                onChange={(event) =>
                  setPartForm((current) => ({
                    ...current,
                    unitPrice: event.target.value,
                  }))
                }
              />
              <button className="compact-button" type="submit" disabled={busy === "part"}>
                {busy === "part" ? "Agregando…" : "Agregar"}
              </button>
            </form>
          ) : null}

          {detail.warehouses.length > 0 &&
          proposedParts > 0 &&
          ["approved", "in_progress", "testing", "ready"].includes(order.status) ? (
            <div className="consume-parts-row">
              <select
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
              >
                <option value="">Selecciona almacén</option>
                {detail.warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
              <button
                className="compact-button"
                type="button"
                disabled={busy === "consume"}
                onClick={() => void consumeParts()}
              >
                {busy === "consume" ? "Procesando…" : "Consumir inventario"}
              </button>
            </div>
          ) : null}

          <div className="detail-list">
            {parts.length === 0 ? (
              <p className="settings-empty">Sin repuestos registrados.</p>
            ) : (
              parts.map((row) => (
                <div className="detail-list-row" key={row.id}>
                  <div>
                    <strong>{row.description}</strong>
                    <span>
                      {Number(row.quantity)} × {money(row.unit_price)} · {row.part_status}
                    </span>
                  </div>
                  <strong>{money(row.total)}</strong>
                </div>
              ))
            )}
          </div>
        </article>
      </section>

      {order.status === "ready" && !detail.delivery ? (
        <section className="panel-card delivery-panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Entrega</span>
              <h2>Registrar entrega formal</h2>
            </div>
          </div>

          <form className="delivery-form" onSubmit={recordDelivery}>
            <label>
              Recibido por
              <input
                required
                value={deliveredTo}
                onChange={(event) => setDeliveredTo(event.target.value)}
              />
            </label>

            <label>
              Documento
              <input
                placeholder="DNI / CE / otro"
                value={recipientDocument}
                onChange={(event) => setRecipientDocument(event.target.value)}
              />
            </label>

            <label className="detail-span-2">
              Condición final
              <input
                value={finalCondition}
                onChange={(event) => setFinalCondition(event.target.value)}
              />
            </label>

            <label className="detail-span-2">
              Notas de entrega
              <textarea
                rows={3}
                value={deliveryNotes}
                onChange={(event) => setDeliveryNotes(event.target.value)}
              />
            </label>

            <label>
              Días de garantía
              <input
                type="number"
                min="0"
                max="3650"
                value={warrantyDays}
                onChange={(event) => setWarrantyDays(event.target.value)}
              />
            </label>

            <label>
              Notas de garantía
              <input
                value={warrantyNotes}
                onChange={(event) => setWarrantyNotes(event.target.value)}
              />
            </label>

            <label className="detail-checkbox detail-span-2">
              <input
                type="checkbox"
                checked={customerConformity}
                onChange={(event) => setCustomerConformity(event.target.checked)}
              />
              Cliente conforme con la entrega
            </label>

            <div className="form-actions detail-span-full">
              <button
                className="primary-button"
                type="submit"
                disabled={busy === "delivery"}
              >
                {busy === "delivery" ? "Registrando…" : "Registrar entrega"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {detail.delivery ? (
        <section className="panel-card delivery-record">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Entrega completada</span>
              <h2>Constancia de entrega</h2>
            </div>
            <span className="status-badge status-badge-approved">
              {detail.delivery.customer_conformity ? "Conforme" : "Sin conformidad"}
            </span>
          </div>

          <dl className="detail-definition-grid">
            <div>
              <dt>Recibido por</dt>
              <dd>{detail.delivery.delivered_to ?? "—"}</dd>
            </div>
            <div>
              <dt>Documento</dt>
              <dd>{detail.delivery.recipient_document ?? "—"}</dd>
            </div>
            <div>
              <dt>Entrega</dt>
              <dd>{datetime(detail.delivery.delivered_at)}</dd>
            </div>
            <div>
              <dt>Garantía</dt>
              <dd>
                {order.warranty_days > 0
                  ? `${order.warranty_days} días · hasta ${order.warranty_until ?? "—"}`
                  : "Sin garantía registrada"}
              </dd>
            </div>
            <div className="detail-span-full">
              <dt>Condición final</dt>
              <dd>{detail.delivery.final_condition ?? "—"}</dd>
            </div>
            <div className="detail-span-full">
              <dt>Notas</dt>
              <dd>{detail.delivery.delivery_notes ?? "—"}</dd>
            </div>
          </dl>
        </section>
      ) : null}

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Evidencias</span>
            <h2>Fotos, videos y documentos</h2>
          </div>
        </div>

        {!isClosed ? (
          <form className="evidence-form" onSubmit={uploadEvidence}>
            <label className="detail-span-full">
              Archivo
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,application/pdf"
                onChange={(event) => setEvidenceFile(event.target.files?.[0] ?? null)}
              />
            </label>

            <label>
              Etapa
              <select
                value={evidenceStage}
                onChange={(event) => {
                  const stage = event.target.value;
                  setEvidenceStage(stage);
                  if (stage !== "intake") setEvidenceInitial(false);
                }}
              >
                <option value="intake">Recepción</option>
                <option value="diagnosis">Diagnóstico</option>
                <option value="repair">Servicio</option>
                <option value="testing">Pruebas</option>
                <option value="delivery">Entrega</option>
                <option value="claim">Garantía / reclamo</option>
              </select>
            </label>

            <label className="detail-span-2">
              Descripción
              <input
                value={evidenceDescription}
                onChange={(event) => setEvidenceDescription(event.target.value)}
              />
            </label>

            <label className="detail-checkbox">
              <input
                type="checkbox"
                checked={evidenceVisible}
                onChange={(event) => setEvidenceVisible(event.target.checked)}
              />
              Visible para cliente
            </label>

            <label className="detail-checkbox">
              <input
                type="checkbox"
                disabled={evidenceStage !== "intake"}
                checked={evidenceInitial}
                onChange={(event) => setEvidenceInitial(event.target.checked)}
              />
              Evidencia inicial
            </label>

            <div className="form-actions">
              <button className="primary-button" type="submit" disabled={busy === "evidence"}>
                {busy === "evidence" ? "Subiendo…" : "Subir evidencia"}
              </button>
            </div>
          </form>
        ) : null}

        <div className="evidence-grid">
          {evidence.length === 0 ? (
            <p className="settings-empty">Sin evidencias registradas.</p>
          ) : (
            evidence.map((row) => (
              <article className="evidence-card" key={row.id}>
                <div>
                  <span className="status-badge">{row.evidence_stage}</span>
                  <strong>{row.original_filename ?? row.media_type}</strong>
                  <span>{row.description ?? "Sin descripción"}</span>
                </div>
                <div className="evidence-card-meta">
                  <span>{fileSize(row.file_size)}</span>
                  <span>{datetime(row.captured_at)}</span>
                  {row.visible_to_customer ? <span>Visible al cliente</span> : null}
                </div>
                {evidenceLinks[row.id] ? (
                  <a
                    className="compact-button"
                    href={evidenceLinks[row.id]}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Abrir
                  </a>
                ) : null}
              </article>
            ))
          )}
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Historial</span>
            <h2>Trazabilidad de estados</h2>
          </div>
        </div>

        <div className="timeline">
          {history.map((row) => (
            <article className="timeline-row" key={row.id}>
              <span className="timeline-dot" />
              <div>
                <strong>{STATUS_LABELS[row.new_status]}</strong>
                <span>
                  {datetime(row.created_at)}
                  {row.changed_by_email ? ` · ${row.changed_by_email}` : ""}
                </span>
                {row.note ? <p>{row.note}</p> : null}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
